
import React, { CSSProperties, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './_dealer-management.module.scss';
import { GenericGrid } from '@amsanalytics/react-shared-library';
import { useSelector } from 'react-redux';
import { AppState } from '../../../Store/rootReducer';
import { createDealer, loadAllDealerGroups, loadAllDealers, updateDealer } from '../../../Store/Dealers/actions';
import { useDispatch } from 'react-redux';
import { GridCell, GridExpandChangeEvent, GridRowProps } from '@progress/kendo-react-grid';
import DealerDetail from './DealerDetail/DealerDetail';
import { Dealer, UpdateDealer } from '../../../Models/Dealer';
import { loadAllDealerReports, loadDealerReports } from '../../../Store/PerformanceReport/actions';
import moment from 'moment';
import DealerEditorDialog, { DealerEditInfo } from './DealerEditorDialog/DealerEditorDialog';
import { includes } from 'ramda';
import LoadingPanel from '../../Controls/LoadingPanel/LoadingPanel';


export interface DealerManagementProps {

}


export default function DealerManagement(props: DealerManagementProps) {  
  const dealers = useSelector((state: AppState) => state.dealers.dealers);
  const dealerGroups = useSelector((state: AppState) => state.dealers.dealerGroups);
  const dealerReports = useSelector((state: AppState) => state.performanceReport.dealerReports);
  const usedClientIds = useSelector((state: AppState) => state.library.usedClientIds);
  const businessTypes = useSelector((state: AppState) => state.library.businessTypes);
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>();
  const [dealersForGrid, setDealersForGrid] = useState<(Dealer & {loading?: boolean, expanded?: boolean})[]>([]);
  const dealerManagementPageLoading = useSelector((state: AppState) => state.shared.dealerManagementPageLoading);
  const dispatch = useDispatch();
  useEffect(() => {    
    dispatch(loadAllDealerGroups());
    dispatch(loadAllDealers());
    dispatch(loadAllDealerReports());
  }, []);

  
  useEffect(() => {
    if(dealers) {
      setDealersForGrid(dealers.map((d, index) => {
        return {
          ...d,
          expanded: (dealersForGrid.find(dg => dg.id === d.id) as any)?.expanded! ?? false,
          updating: false
        }
      }));
    }
  }, [dealers]);


  const rowRender = (row: React.ReactElement, props: GridRowProps) => {
    if (props.rowType === 'data') {
      const dealer: (Dealer & {updating?: boolean, expanded?: boolean}) = dealersForGrid.find(d => d.id === props.dataItem.id)!;
      if (dealer.updating) {
        return (
          <React.Fragment>
            {row}
            <tr style={{position: 'relative', height: '0px'}}>
              <td colSpan={4}> {/* Replace 100 with the actual number of columns */}
                <LoadingPanel style={{ position: 'absolute', width: '100%', textAlign: 'center' }} label='Updating' />
              </td>
            </tr>
          </React.Fragment>
        );
      }
      return row;
    }
    return row;
  }
  
  return (
    <>
      <div className={styles.dealerManagementContainer}>
        <GenericGrid<Dealer>
          disableEditing={false}
          disableAdding={false}
          itemName='Dealers'
          itemNameStyle={{ color: '#d95137' }}
          useIconButtons={true}
          disableGrouping={true}
          items={dealersForGrid.map((d: Dealer) => {
            const dealerGroupsForDealer = dealerGroups?.filter(dg => includes(dg.id, (d.dealerGroupLookupInfoList ?? []).map(dgli => dgli.dealerGroupId))) ?? []; 
            const businessTypesForDealer = businessTypes.filter(bt => includes(bt.id, dealerGroupsForDealer.flatMap(dg => dg.businessTypeIds)));
            return {
              ...d,
              businessTypeNames: businessTypesForDealer.map(bt => bt.name).join(', '),
              dataStatus: dealerReports?.find(dr => dr.clientId === d.id && dr.year === moment().year() && dr.month === moment().month())?.status ?? 'Not Started',
              lastReportDate: d.lastReportDate ? new Date(d.lastReportDate) : new Date(2000, 0, 1)
            }
          }) ?? []}
          selectedField={'selected'}
          onOpenItemEditor={() => {
            setSelectedDealer(null);
          }}
          onItemSelected={(item: Dealer) => {
            if (item) {
              setSelectedDealer(item);              
            }
          }}
          
          onItemExpanded={(event: GridExpandChangeEvent) => {
            setDealersForGrid(dealersForGrid.map(d => {
              if (event.dataItem.id === d.id) {
                return {
                  ...d,
                  expanded: event.value
                }
              }
              return { ...d }
            }))
          }}
          rowRender={rowRender}
          detail={DealerDetail}
          sortDescriptors={[{ field: 'date', dir: 'desc' }]}
          columns={[
            { field: "name", title: "Name"},
            { field: "businessTypeNames", width: "120px", title: "Type" },            
            { field: "dataStatus", width: "200", title: "Data Status"  },
            { field: "activeDate", width: "120", title: "Active Date", format: "{0:d}", preventFiltering: true },
          ]}
        />
      </div>
      {selectedDealer !== undefined && ( // NOTE: Null will open the editor. Undefined will close it.
        <DealerEditorDialog
          dealer={selectedDealer}
          timeZones={['Eastern Standard Time', 'Central Standard Time', 'Mountain Standard Time', 'Pacific Standard Time', 'Alaska Standard Time', 'Hawaii-Aleutian Standard Time']}
          onClose={() => setSelectedDealer(undefined)}
          usedClientIds={usedClientIds}
          onSubmit={(dealer: UpdateDealer) => {     
            if(selectedDealer?.id) {
              setDealersForGrid(dealersForGrid.map(d => {
                if (d.id === selectedDealer.id) {
                  return {
                    ...d,
                    updating: true
                  }
                }
                return d;
              }));
              dispatch(updateDealer(dealer));
            } else {
              dispatch(createDealer(dealer));
            }
            setSelectedDealer(undefined);
          }}
        />
      )}
      {dealerManagementPageLoading && <LoadingPanel label={"Loading Dealers..."} style={{position: 'absolute'}}/>}
    </>  
  );
}
